import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle, Ban, Check, CheckCircle2, ChevronRight, Clock, Copy, DollarSign,
  FileSpreadsheet, Inbox, Info, ListChecks, MinusCircle, Search, UploadCloud, X,
} from 'lucide-react';
import { useSiteContext } from '../../../SiteContext';
import {
  exportJobStatusToExcel,
  generateBatchExportV2ForEmployees,
  generateBatchTaskCSVForEmployees,
  getVistaPayrollApprovedTimesheets,
  pollVistaJob,
} from '../../../api/vistaBatchExport';
import type { VistaExportTypeV2 } from '../../../api/vistaBatchExport';
import type {
  EmployeeTimeSheetWithDays, JobProgressEmployee, JobStatus, MiscPiecePost, RegularPiecePost,
} from '../../../interfaces/V2Interface';
import { JobStatusEnum } from '../../../interfaces/V2Interface';
import VistaErrorDetail, { VistaErrorDetailProps } from './VistaErrorDetail';
import {
  batchExistsButUnknown, batchIdsOf, classifyFailure, deriveCounts, extractVistaMessage,
  formatName, groupFailures, hashHue, initialsOf, isBlocked, outcomeOf, PIECE_STATUS_LABEL,
  exportDropGaps, silentWarnings, volumeOf,
} from './vistaExportHelpers';
import config from '../../../configLoader';

/* ─────────────────────────────────────────────────────────────────── copy */

/**
 * `translator.translate` answers `undefined` for a key that is not in strings.json —
 * its own "N/A" fallback is unreachable dead code — and its return type is `any`, so
 * TypeScript will not catch that leaking into JSX. Every lookup goes through here.
 */
const useTx = () => {
  const { translator } = useSiteContext();
  return useCallback((key: string, fallback: string): string => {
    const v = translator.translate(key);
    return typeof v === 'string' && v && v !== 'N/A' ? v : fallback;
  }, [translator]);
};

const EXPORT_TYPES: Array<{ value: VistaExportTypeV2; key: string; label: string }> = [
  { value: 'both', key: 'SendTimeAndPieces', label: 'Time and pieces' },
  { value: 'time', key: 'SendTimeOnly', label: 'Time only' },
  { value: 'pieceworks', key: 'SendPiecesOnly', label: 'Pieces only' },
];

/* ─────────────────────────────────────────────────────────────────── props */

export interface VistaExportModalProps {
  supervisorID: number;
  /** "DAVILA, JUAN" */
  supervisorName: string;
  supervisorAltID: string;
  locationLabel?: string;
  payPeriodID: number;
  week: number;
  year: number;
  /** "Aug 13 – 19, 2026" */
  dateRange: string;
  onClose: () => void;
  onToast: (msg: string) => void;
}

type Phase = 'loading' | 'loadFailed' | 'select' | 'confirm' | 'starting' | 'running' | 'terminal';

/**
 * Vista export for one supervisor's crew.
 *
 * BLOCKING BY DESIGN. Once the job starts the modal cannot be dismissed, because
 * nothing outside it can carry the job: the board fully unmounts on navigation and
 * there is no server-side cancel, so a dismissable modal would orphan a running push
 * to Vista with no way back to it. The trade is deliberate — see the handoff README.
 */
const VistaExportModal: React.FC<VistaExportModalProps> = ({
  supervisorID, supervisorName, supervisorAltID, locationLabel,
  payPeriodID, week, year, dateRange, onClose, onToast,
}) => {
  const tx = useTx();

  const [phase, setPhase] = useState<Phase>('loading');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [employees, setEmployees] = useState<EmployeeTimeSheetWithDays[]>([]);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [q, setQ] = useState('');
  const [exportType, setExportType] = useState<VistaExportTypeV2>('both');
  const [job, setJob] = useState<JobStatus | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const [openRows, setOpenRows] = useState<Set<string>>(new Set());
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const [errorDetail, setErrorDetail] = useState<VistaErrorDetailProps | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [csvBusy, setCsvBusy] = useState(false);
  const [excelBusy, setExcelBusy] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  /* ── load the crew ───────────────────────────────────────────────────── */

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const rows = await getVistaPayrollApprovedTimesheets(supervisorID, payPeriodID);
        if (!alive) return;
        const sorted = [...rows].sort((a, b) => Number(a.altEmployeeID ?? 0) - Number(b.altEmployeeID ?? 0));
        setEmployees(sorted);
        // Opt-out: everyone exportable starts checked.
        setSel(new Set(sorted.filter((e) => !isBlocked(e)).map((e) => String(e.employeeID))));
        setPhase('select');
      } catch (err) {
        if (!alive) return;
        setLoadError(err instanceof Error ? err.message : 'Could not load this crew.');
        setPhase('loadFailed');
      }
    })();
    return () => { alive = false; };
  }, [supervisorID, payPeriodID]);

  // Nothing outside this component owns the poll, so it must stop when we unmount.
  useEffect(() => () => abortRef.current?.abort(), []);

  /* ── derived ─────────────────────────────────────────────────────────── */

  const blocked = useMemo(() => employees.filter(isBlocked), [employees]);
  const selectable = useMemo(() => employees.filter((e) => !isBlocked(e)), [employees]);

  const shown = useMemo(() => {
    const raw = q.trim().toLowerCase();
    if (!raw) return employees;
    const id = raw.startsWith('#') ? raw.slice(1) : raw;
    return employees.filter((e) =>
      formatName(e.name).toLowerCase().includes(raw)
      || (id !== '' && (e.altEmployeeID ?? '').toLowerCase().includes(id)));
  }, [employees, q]);

  const shownSelectable = useMemo(() => shown.filter((e) => !isBlocked(e)), [shown]);
  const shownSelCount = shownSelectable.reduce((n, e) => n + (sel.has(String(e.employeeID)) ? 1 : 0), 0);
  const allShownOn = shownSelectable.length > 0 && shownSelCount === shownSelectable.length;
  const someShown = shownSelCount > 0 && !allShownOn;
  const filtering = q.trim().length > 0;

  const chosen = useMemo(
    () => selectable.filter((e) => sel.has(String(e.employeeID))),
    [selectable, sel]);

  const planned = useMemo(() => chosen.reduce(
    (acc, e) => { const v = volumeOf(e); return { time: acc.time + v.time, pieces: acc.pieces + v.pieces }; },
    { time: 0, pieces: 0 }), [chosen]);

  // TAS-515/517 — hourly punches in this selection the exporter matches to no branch and
  // therefore sends nothing for. Since TAS-517 widened the service-order test, service
  // orders are NOT among them; what is left is the uncoded punch (usually a job with no
  // phase). See exportDropGaps for why the volume counts above do not include these.
  //
  // The gate stays even though the predicate is now tenant-neutral: this modal is NPM's
  // and nothing in this project may change what another tenant sees.
  const dropGaps = useMemo(
    () => (config.tehPunchesAreHourly ? exportDropGaps(chosen) : []),
    [chosen]);
  const dropGapPunches = useMemo(() => dropGaps.reduce((n, g) => n + g.punches, 0), [dropGaps]);

  const counts = useMemo(() => (job ? deriveCounts(job) : null), [job]);
  const outcome = useMemo(() => (job && counts ? outcomeOf(job, counts) : null), [job, counts]);
  const failureGroups = useMemo(() => groupFailures(job?.progressData), [job]);
  const warnings = useMemo(() => silentWarnings(job?.progressData), [job]);
  const miscSkipped = counts?.miscSkipped ?? 0;

  /* ── actions ─────────────────────────────────────────────────────────── */

  const toggle = (id: string) => setSel((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleAllShown = (on: boolean) => setSel((prev) => {
    const next = new Set(prev);
    shownSelectable.forEach((e) => { const id = String(e.employeeID); if (on) next.add(id); else next.delete(id); });
    return next;
  });

  const start = async () => {
    setPhase('starting');
    setStartError(null);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const { jobID } = await generateBatchExportV2ForEmployees(
        supervisorID, payPeriodID, chosen.map((e) => String(e.employeeID)), exportType);
      setPhase('running');
      const final = await pollVistaJob(jobID, setJob, controller.signal);
      setJob(final);
      setPhase('terminal');
    } catch (err) {
      if (controller.signal.aborted) return;   // unmounted; nothing to render into
      setStartError(err instanceof Error ? err.message : 'The export could not be started.');
      setPhase(job ? 'terminal' : 'select');
    }
  };

  const downloadPieces = async () => {
    setCsvBusy(true);
    try {
      await generateBatchTaskCSVForEmployees(
        supervisorID, payPeriodID, chosen.map((e) => String(e.employeeID)), year, week, supervisorAltID);
      onToast(tx('VistaPiecesDownloaded', 'Pieces spreadsheet downloaded.'));
    } catch {
      setStartError(tx('VistaPiecesFailed', 'The pieces spreadsheet could not be generated.'));
    } finally {
      setCsvBusy(false);
    }
  };

  const downloadStatusExcel = async () => {
    if (!job) return;
    setExcelBusy(true);
    try {
      await exportJobStatusToExcel(job.jobStatusID);
      onToast(tx('VistaStatusDownloaded', 'Export status workbook downloaded.'));
    } catch {
      onToast(tx('VistaStatusFailed', 'The status workbook could not be generated.'));
    } finally {
      setExcelBusy(false);
    }
  };

  const copy = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      window.setTimeout(() => setCopied((c) => (c === label ? null : c)), 1800);
      onToast(`${label} copied.`);
    } catch {
      onToast(tx('VistaCopyFailed', 'Could not copy — select the number and copy it manually.'));
    }
  };

  const showPieceError = (emp: JobProgressEmployee, post: RegularPiecePost | MiscPiecePost, source: 'piece' | 'misc') => {
    const failedStep = source === 'misc' ? (post as MiscPiecePost).failedStep : undefined;
    const reason = classifyFailure(post.errorMessage, source, failedStep);
    const orderNo = source === 'misc'
      ? (post as MiscPiecePost).vistaOrderNoAfterCreate
      : (post as RegularPiecePost).vistaBody?.OrderNo;
    setErrorDetail({
      title: reason.title,
      meta: [`${emp.lastName ?? ''}, ${emp.firstName ?? ''}`.replace(/^, |, $/, ''),
        emp.altEmployeeID ? `#${emp.altEmployeeID}` : '',
        orderNo != null ? `order ${orderNo}` : ''].filter(Boolean).join(' · '),
      plain: reason.plain,
      vistaSaid: extractVistaMessage(post.errorDetails, post.errorMessage),
      raw: post.errorDetails ?? post.errorMessage,
      onClose: () => setErrorDetail(null),
    });
  };

  /* ── shared chrome ───────────────────────────────────────────────────── */

  const metaLine = [supervisorName, locationLabel, `Week ${week}`, dateRange].filter(Boolean).join(' · ');
  const closable = phase === 'loading' || phase === 'loadFailed' || phase === 'select'
    || phase === 'confirm' || phase === 'terminal';

  const head = (icon: React.ReactNode, tone: string, title: string, sub: string) => (
    <div className="modal-head">
      <span className={`mi${tone ? ` ${tone}` : ''}`}>{icon}</span>
      <div>
        <h3>{title}</h3>
        <div className="ms">{sub}</div>
      </div>
      {closable && <button className="x" onClick={onClose} type="button" aria-label="Close"><X /></button>}
    </div>
  );

  const miscBanner = (n: number) => n > 0 && (
    <div className="vx-note info">
      <MinusCircle />
      <span>
        <b>{tx('VistaMiscDisabledTitle', 'Misc pieces are not being posted to Vista.')}</b>{' '}
        {tx('VistaMiscDisabledBody',
          `Posting is switched off in the backend, so all ${n} came back skipped on this run — nothing was rejected and nothing is missing from the batch. When the switch is turned back on they will post and count here.`)
          .replace('{n}', String(n))}
      </span>
    </div>
  );

  /* ── modal width follows the body ────────────────────────────────────── */

  const size = phase === 'running' || phase === 'terminal' ? 'xl'
    : phase === 'starting' ? '' : 'wide';

  return (
    <>
      <div className="trb-modal-bg" onClick={() => { if (closable) onClose(); }}>
        <div className={`trb-modal vx-modal${size ? ` ${size}` : ''}`} onClick={(e) => e.stopPropagation()}>

          {/* ── loading ─────────────────────────────────────────────────── */}
          {phase === 'loading' && (
            <>
              {head(<UploadCloud />, '', tx('VistaExport', 'Vista Export'), metaLine)}
              <div className="modal-body">
                <div className="vx-starting">
                  <div className="vx-spin" />
                  <span className="t">{tx('LoadingEmployees', 'Loading employees…')}</span>
                </div>
              </div>
            </>
          )}

          {/* ── the crew could not be loaded ────────────────────────────── */}
          {phase === 'loadFailed' && (
            <>
              {head(<AlertTriangle />, 'bad', tx('FetchError', 'Fetch Error'), metaLine)}
              <div className="modal-body">
                <div className="vx-note bad"><AlertTriangle /><span>{loadError}</span></div>
              </div>
              <div className="modal-foot tr-exp-foot">
                <button className="tr-btn" onClick={onClose} type="button">{tx('Close', 'Close')}</button>
              </div>
            </>
          )}

          {/* ── selection ───────────────────────────────────────────────── */}
          {phase === 'select' && (
            <>
              {head(<UploadCloud />, '', tx('VistaExport', 'Vista Export'), metaLine)}
              <div className="modal-body tr-exp">
                {startError && <div className="vx-note bad"><AlertTriangle /><span>{startError}</span></div>}

                {selectable.length === 0 ? (
                  <>
                    {employees.length > 0 && renderList()}
                    <div className="vx-deadend">
                      <span className="tile"><Ban /></span>
                      <h4>{tx('VistaAllBlockedTitle', 'Nothing to export for this crew')}</h4>
                      <p>
                        {tx('VistaAllBlockedBody',
                          `Every employee on ${supervisorName}'s crew has a miscellaneous task still awaiting approval, so there is nothing this export can send.`)
                          .replace('{supervisor}', supervisorName)}
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="tr-exp-intro">
                      {tx('VistaSelectIntro',
                        'Everyone is selected. Uncheck anyone you want to hold back — held-back employees stay exportable next run.')}
                    </p>

                    <div className="tr-exp-search">
                      <Search />
                      <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder={tx('VistaFilterPlaceholder', 'Filter by employee name or ID…')}
                        autoComplete="off"
                      />
                    </div>

                    <label className="tr-exp-all">
                      <input
                        type="checkbox"
                        ref={(el) => { if (el) el.indeterminate = someShown; }}
                        checked={allShownOn}
                        onChange={(e) => toggleAllShown(e.target.checked)}
                        disabled={shownSelectable.length === 0}
                      />
                      <span className="tr-exp-all-lab">
                        {filtering ? tx('SelectAllShown', 'Select all shown') : tx('SelectAll', 'Select all')}
                      </span>
                      <span className="tr-exp-count">
                        <b>{sel.size}</b> of <b>{selectable.length}</b> {tx('Selected', 'selected')}
                        {filtering && <> &middot; <b>{shown.length}</b> {tx('Shown', 'shown')}</>}
                      </span>
                    </label>

                    {renderList()}

                    {blocked.length > 0 && (
                      <div className="vx-note info">
                        <Info />
                        <span>
                          <b>{`${blocked.length} of ${employees.length} ${tx('VistaCannotExportNoteLead', 'cannot be exported.')}`}</b>{' '}
                          {tx('VistaCannotExportNoteBody',
                            'They have a miscellaneous task still awaiting approval. They stay in the list, greyed out, and are left out of this run.')}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="modal-foot tr-exp-foot vx-foot">
                {selectable.length > 0 && (
                  <>
                    <span className="vx-seglab">{tx('Send', 'Send')}</span>
                    <div className="vx-seg">
                      {EXPORT_TYPES.map((t) => (
                        <button
                          key={t.value}
                          type="button"
                          className={exportType === t.value ? 'on' : ''}
                          onClick={() => setExportType(t.value)}
                        >
                          {tx(t.key, t.label)}
                        </button>
                      ))}
                    </div>
                  </>
                )}
                <span className="vx-grow" />
                <button
                  className="tr-btn"
                  onClick={downloadPieces}
                  disabled={csvBusy || chosen.length === 0}
                  type="button"
                  title="Pre-flight spreadsheet of the piece claims. Downloads a file; nothing is sent to Vista."
                >
                  <FileSpreadsheet /> {csvBusy ? tx('Generating', 'Generating…') : tx('DownloadPieces', 'Download pieces (.xlsx)')}
                </button>
                <button className="tr-btn" onClick={onClose} type="button">{tx('Cancel', 'Cancel')}</button>
                <button
                  className="tr-btn primary"
                  onClick={() => setPhase('confirm')}
                  disabled={chosen.length === 0}
                  type="button"
                >
                  <UploadCloud /> {tx('ExportN', 'Export {n}').replace('{n}', String(chosen.length))}
                </button>
              </div>
            </>
          )}

          {/* ── confirm ─────────────────────────────────────────────────── */}
          {phase === 'confirm' && (
            <>
              {head(<AlertTriangle />, 'warn', tx('VistaConfirmTitle', 'Start this export?'), metaLine)}
              <div className="modal-body vx-confirm">
                <div className="vx-sumcard">
                  <div className="vx-sumrow">
                    <span className="lb">{tx('Crew', 'Crew')}</span>
                    <span className="vl">{supervisorName}</span>
                  </div>
                  <div className="vx-sumrow">
                    <span className="lb">{tx('Employees', 'Employees')}</span>
                    <span className="vl mono">
                      {chosen.length}
                      {blocked.length > 0 && (
                        <span style={{ fontWeight: 400 }}> {` (${blocked.length} held back)`}</span>
                      )}
                    </span>
                  </div>
                  <div className="vx-sumrow">
                    <span className="lb">{tx('Sending', 'Sending')}</span>
                    <span className="vl">{tx(EXPORT_TYPES.find((t) => t.value === exportType)!.key,
                      EXPORT_TYPES.find((t) => t.value === exportType)!.label)}</span>
                  </div>
                  {exportType !== 'pieceworks' && (
                    <div className="vx-sumrow">
                      <span className="lb">{tx('TimeEntries', 'Time entries')}</span>
                      <span className="vl mono">{planned.time}</span>
                    </div>
                  )}
                  {exportType !== 'time' && (
                    <div className="vx-sumrow">
                      <span className="lb">{tx('PieceClaims', 'Piece claims')}</span>
                      <span className="vl mono">{planned.pieces}</span>
                    </div>
                  )}
                </div>

                {/* TAS-515/517 — said before the run, not after: this is the TAS-448 failure
                    mode (a clean "all posted" over hours that never arrived), and the only
                    moment it can be acted on is while the export can still be called off. */}
                {dropGaps.length > 0 && (
                  <div className="vx-note warn vx-dropgap">
                    <AlertTriangle />
                    <span>
                      <b>
                        {dropGapPunches} hourly punch{dropGapPunches === 1 ? '' : 'es'} will not be sent
                        {dropGaps.length > 1 ? ` (${dropGaps.length} employees)` : ''}.
                      </b>{' '}
                      These punches carry no job and phase, no cost code and no service order, so the
                      export has nowhere to put them: it sends nothing for them and still reports
                      success. The totals above do not count them. Fix the coding on the timesheet, or
                      do not treat a clean result as proof these hours arrived.
                      <ul className="vx-dropgap-list">
                        {dropGaps.map((g) => (
                          <li key={g.employeeName}>
                            <b>{g.employeeName}</b>
                            <span>{g.reasons.join(' · ')}</span>
                          </li>
                        ))}
                      </ul>
                    </span>
                  </div>
                )}

                <div className="vx-note info">
                  <Info />
                  <span>
                    {tx('VistaNoCancelNote',
                      'Once it starts, the job runs on the server and cannot be cancelled. This window stays open until it finishes.')}
                  </span>
                </div>
              </div>

              <div className="modal-foot tr-exp-foot">
                <button className="tr-btn" onClick={() => setPhase('select')} type="button">{tx('Back', 'Back')}</button>
                <button className="tr-btn" onClick={onClose} type="button">{tx('Cancel', 'Cancel')}</button>
                <button className="tr-btn primary" onClick={start} type="button">
                  <UploadCloud /> {tx('StartExport', 'Start export')}
                </button>
              </div>
            </>
          )}

          {/* ── starting ────────────────────────────────────────────────── */}
          {phase === 'starting' && (
            <>
              {head(<UploadCloud />, '', tx('StartingExport', 'Starting export…'), metaLine)}
              <div className="modal-body">
                <div className="vx-starting">
                  <div className="vx-spin" />
                  <span className="t">{tx('StartingExport', 'Starting export…')}</span>
                  <span className="s">{tx('VistaStartingSub', 'Creating the job. Vista has not been contacted yet.')}</span>
                </div>
              </div>
            </>
          )}

          {/* ── running ─────────────────────────────────────────────────── */}
          {phase === 'running' && (
            <>
              {head(<UploadCloud />, '', tx('ProcessingExport', 'Processing export…'), metaLine)}
              <div className="modal-body vx-prog">{renderProgress()}</div>
              <div className="modal-foot tr-exp-foot">
                <span className="vx-grow" style={{ fontSize: 12.5, color: 'var(--text-3)' }}>
                  {tx('VistaRunsOnServer', 'Runs on the server — this window stays open until it finishes.')}
                </span>
              </div>
            </>
          )}

          {/* ── terminal ────────────────────────────────────────────────── */}
          {phase === 'terminal' && job && counts && renderTerminal()}
        </div>
      </div>

      {errorDetail && <VistaErrorDetail {...errorDetail} />}
    </>
  );

  /* ────────────────────────────────────────────────────── render helpers */

  function renderList() {
    return (
      <div className="tr-exp-list">
        {shown.length === 0 ? (
          <div className="tr-noresult">
            <Inbox />
            <p>No employee matches &ldquo;<b>{q.trim()}</b>&rdquo;.</p>
          </div>
        ) : shown.map((e) => {
          const id = String(e.employeeID);
          const name = formatName(e.name);
          const hue = hashHue(name);
          const vol = volumeOf(e);
          const sub = `#${e.altEmployeeID} · ${vol.time} time · ${vol.pieces} pieces`;
          const av = (
            <span className="tr-exp-av" style={{ background: `hsl(${hue} 60% 92%)`, color: `hsl(${hue} 55% 38%)` }}>
              {initialsOf(e.name)}
            </span>
          );
          const who = (
            <span className="tr-exp-who">
              <span className="tr-exp-nm">{name}</span>
              <span className="tr-exp-sub">{sub}</span>
            </span>
          );

          // A blocked row is a <div>, not a <label> — nothing about it should be clickable.
          if (isBlocked(e)) {
            return (
              <div
                key={id}
                className="tr-exp-row off"
                title={tx('VistaCannotExportTooltip', 'This employee has a miscellaneous task still awaiting approval.')}
              >
                <input type="checkbox" checked={false} disabled readOnly />
                {av}{who}
                <span className="vx-offtag"><Ban />{tx('VistaCannotExportTag', 'Cannot export')}</span>
              </div>
            );
          }

          const on = sel.has(id);
          return (
            <label key={id} className={`tr-exp-row${on ? ' on' : ''}`}>
              <input type="checkbox" checked={on} onChange={() => toggle(id)} />
              {av}{who}
            </label>
          );
        })}
      </div>
    );
  }

  function renderProgress() {
    const pd = job?.progressData;
    const status = (job?.status ?? JobStatusEnum.PENDING).toLowerCase();
    const pct = job?.progress ?? 0;

    return (
      <>
        <div className="vx-prog-top">
          <span className={`vx-pill ${status}`}><span className="d" />{job?.status ?? 'PENDING'}</span>
          <span className="vx-bar"><i style={{ width: `${pct}%` }} /></span>
          <span className="vx-pct">{pct}%</span>
        </div>

        {job?.currentStep && (
          <div className="vx-step">
            {job.status === JobStatusEnum.PROCESSING && <span className="vx-spin sm" />}
            <span>{job.currentStep}</span>
          </div>
        )}

        {pd?.timecardBatchCreationError && (
          <div className="vx-note bad">
            <AlertTriangle />
            <span><b>{tx('VistaTimecardBatchFailed', 'The timecard batch could not be created.')}</b>{' '}{pd.timecardBatchCreationError}</span>
          </div>
        )}

        {!pd?.employees?.length ? (
          <div className="vx-note info">
            <Info />
            <span>
              {tx('VistaNoProgressDetail',
                'This run is not reporting per-employee detail, so only the overall step is available. The batch is still posting normally — the totals arrive when it finishes.')}
            </span>
          </div>
        ) : (
          <>
            <div className="vx-tiles">
              <div className="vx-tile blue">
                <span className="lb"><Clock />{tx('TimeBatch', 'Time batch')}</span>
                <span className="vl">{pd.completedTimeBatch ?? 0} / {pd.totalTimeBatchEntries ?? 0}</span>
                <span className="sb">
                  {pd.timecardBatchCreation === 'completed' ? tx('BatchCreated', 'batch created')
                    : pd.timecardBatchCreation === 'failed' ? tx('BatchFailed', 'batch failed')
                      : tx('CreatingBatch', 'creating batch…')}
                </span>
              </div>
              <div className="vx-tile">
                <span className="lb"><DollarSign />{tx('Pieces', 'Pieces')}</span>
                <span className="vl">{pd.completedPieces ?? 0} / {pd.totalPieces ?? 0}</span>
                {!!counts?.failed && <span className="sb">{counts.failed} {tx('rejected', 'rejected')}</span>}
              </div>
              <div className={`vx-tile${miscSkipped > 0 ? ' off' : ''}`}>
                <span className="lb"><ListChecks />{tx('MiscPieces', 'Misc pieces')}</span>
                <span className="vl">
                  {miscSkipped > 0
                    ? <s>{pd.completedMiscPieces ?? 0} / {pd.totalMiscPieces ?? 0}</s>
                    : <>{pd.completedMiscPieces ?? 0} / {pd.totalMiscPieces ?? 0}</>}
                </span>
                {miscSkipped > 0 && <span className="sb">{tx('SkippedPostingDisabled', 'skipped — posting disabled')}</span>}
              </div>
            </div>

            {miscBanner(miscSkipped)}
            {renderEmployeeTable()}
          </>
        )}
      </>
    );
  }

  function renderEmployeeTable() {
    const rows = job?.progressData?.employees ?? [];
    return (
      <div className="vx-etable">
        <div className="vx-eth">
          <span>{tx('Employee', 'Employee')}</span>
          <span>{tx('Time', 'Time')}</span>
          <span>{tx('Pieces', 'Pieces')}</span>
          <span>{tx('MiscPieces', 'Misc Pieces')}</span>
          <span />
        </div>
        {rows.map((emp, i) => {
          const key = emp.altEmployeeID ?? String(i);
          const posts: Array<{ post: RegularPiecePost | MiscPiecePost; source: 'piece' | 'misc' }> = [
            ...(emp.regularPiecePosts ?? []).map((p) => ({ post: p, source: 'piece' as const })),
            ...(emp.miscPiecePosts ?? []).map((p) => ({ post: p, source: 'misc' as const })),
          ];
          const open = openRows.has(key);
          return (
            <div key={key} className={`vx-erow${open ? ' open' : ''}`}>
              <div className="vx-etr">
                <span className="who">
                  <span className="nm">{`${emp.lastName ?? ''}, ${emp.firstName ?? ''}`.replace(/^, |, $/, '')}</span>
                  <span className="id">#{emp.altEmployeeID}</span>
                </span>
                {timeCell(emp)}
                {pieceCell(emp, 'piece')}
                {pieceCell(emp, 'misc')}
                {posts.length > 0 ? (
                  <button
                    className="vx-echev"
                    type="button"
                    aria-label={open ? 'Collapse' : 'Expand'}
                    onClick={() => setOpenRows((prev) => {
                      const next = new Set(prev);
                      if (next.has(key)) next.delete(key); else next.add(key);
                      return next;
                    })}
                  >
                    <ChevronRight />
                  </button>
                ) : <span />}
              </div>
              {open && posts.length > 0 && (
                <div className="vx-edet">
                  <div className="vx-subhead"><ListChecks />{tx('PiecePosts', 'Piece posts')} ({posts.length})</div>
                  <table className="vx-ptbl">
                    <thead>
                      <tr>
                        <th>{tx('OrderNo', 'Order No')}</th>
                        <th className="r">{tx('Amount', 'Amount')}</th>
                        <th>{tx('Type', 'Type')}</th>
                        <th>{tx('Status', 'Status')}</th>
                        <th>{tx('Detail', 'Detail')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {posts.map(({ post, source }, idx) => {
                        const orderNo = source === 'misc'
                          ? (post as MiscPiecePost).vistaOrderNoAfterCreate
                          : (post as RegularPiecePost).vistaBody?.OrderNo;
                        const amount = source === 'piece' ? (post as RegularPiecePost).vistaBody?.AmountClaimed : undefined;
                        return (
                          <tr key={`${source}-${idx}`}>
                            <td>{orderNo ?? '—'}</td>
                            <td className="r">{amount != null ? `$${Number(amount).toFixed(2)}` : '—'}</td>
                            <td>{source === 'misc' ? tx('misc', 'misc') : tx('regular', 'regular')}</td>
                            <td><span className={`st ${post.status}`}>{PIECE_STATUS_LABEL[post.status]}</span></td>
                            <td>
                              {post.status === 'failed' ? (
                                <button className="lnk" type="button" onClick={() => showPieceError(emp, post, source)}>
                                  {tx('ViewError', 'View error')}
                                </button>
                              ) : post.status === 'skipped' ? tx('PostingDisabled', 'posting disabled') : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  /**
   * The Time cell.
   *
   * `timeBatchError` is stamped onto EVERY employee in the job by the API, not just the
   * ones in the failed chunk, so it is suppressed for anyone with no time entries —
   * otherwise the table shows an error against people who had nothing to post. The
   * failure itself is reported once, at job level, in the terminal failure groups.
   */
  function timeCell(emp: JobProgressEmployee) {
    const total = emp.timeBatchTotal ?? 0;
    const done = emp.timeBatchCompleted ?? 0;
    if (total === 0) return <span className="vx-cell wait">—</span>;
    const n = <span className="ct">{done} / {total}</span>;
    if (emp.timeBatchError) return <span className="vx-cell bad"><X />{n}</span>;
    if (done >= total) return <span className="vx-cell done"><Check />{n}</span>;
    return <span className="vx-cell wait"><span className="vx-spin sm" />{n}</span>;
  }

  function pieceCell(emp: JobProgressEmployee, source: 'piece' | 'misc') {
    const posts: Array<RegularPiecePost | MiscPiecePost> =
      (source === 'piece' ? emp.regularPiecePosts : emp.miscPiecePosts) ?? [];
    const total = (source === 'piece' ? emp.piecesTotal : emp.miscPiecesTotal) ?? 0;
    const done = (source === 'piece' ? emp.piecesCompleted : emp.miscPiecesCompleted) ?? 0;
    if (total === 0) return <span className="vx-cell wait">—</span>;

    const failed = posts.filter((p) => p.status === 'failed').length;
    const skipped = posts.filter((p) => p.status === 'skipped').length;
    const n = <span className="ct">{done} / {total}</span>;

    if (failed > 0) {
      const first = posts.find((p) => p.status === 'failed')!;
      return (
        <span className="vx-cell bad">
          <X />{n}
          <button className="lnk" type="button" onClick={() => showPieceError(emp, first, source)}>
            {tx('ViewError', 'View error')}
          </button>
        </span>
      );
    }
    // Switched off in the backend is a state, not a failure — never red.
    if (skipped === total) {
      return <span className="vx-cell skip"><MinusCircle /><span className="ct">{skipped} {tx('skipped', 'skipped')}</span></span>;
    }
    if (done >= total) return <span className="vx-cell done"><Check />{n}</span>;
    return <span className="vx-cell wait"><span className="vx-spin sm" />{n}</span>;
  }

  function renderTerminal() {
    if (!job || !counts) return null;
    const { batchId, keyID } = batchIdsOf(job);
    const unknownBatch = batchExistsButUnknown(job);

    const OUTCOME = {
      success: {
        tone: 'ok', icon: <CheckCircle2 />, mi: 'ok',
        title: tx('VistaDoneTitle', 'Export finished'),
        hl: `${counts.posted} of ${counts.total} ${tx('posted', 'posted')}`,
        sl: tx('VistaSuccessSub', `Every time entry and piece claim for ${chosen.length} employees went into Vista.`)
          .replace('{n}', String(chosen.length)),
      },
      partial: {
        tone: 'part', icon: <AlertTriangle />, mi: 'warn',
        title: tx('VistaPartialTitle', 'Export finished with rejections'),
        hl: `${counts.posted} of ${counts.total} ${tx('posted', 'posted')}`,
        sl: tx('VistaPartialSub', '{n} rejected — the batch is in Vista either way.')
          .replace('{n}', String(counts.failed)),
      },
      timeout: {
        tone: 'bad', icon: <AlertTriangle />, mi: 'bad',
        title: tx('VistaTimeoutTitle', 'Vista stopped responding'),
        hl: tx('VistaTimeoutHeadline', 'Vista stopped responding'),
        sl: tx('VistaTimeoutSub', `The job gave up at ${job.progress}% and stopped there.`).replace('{n}', String(job.progress)),
      },
      batchFailed: {
        tone: 'bad', icon: <AlertTriangle />, mi: 'bad',
        title: tx('VistaBatchFailedTitle', 'Nothing was exported'),
        hl: tx('VistaBatchFailedHeadline', 'The timecard batch was never created'),
        sl: tx('VistaBatchFailedSub', 'Vista refused to open a batch, so no hours and no pieces were sent. Nothing needs undoing.'),
      },
      failed: {
        tone: 'bad', icon: <AlertTriangle />, mi: 'bad',
        title: tx('ExportFailed', 'Export failed'),
        hl: `${counts.posted} of ${counts.total} ${tx('posted', 'posted')}`,
        sl: tx('VistaFailedSub', 'The job stopped before it finished.'),
      },
    }[outcome ?? 'failed'];

    const rd = job.resultData as { error?: string } | null | undefined;

    return (
      <>
        {head(OUTCOME.icon, OUTCOME.mi, OUTCOME.title, `${metaLine} · ${tx(EXPORT_TYPES.find((t) => t.value === exportType)!.key, EXPORT_TYPES.find((t) => t.value === exportType)!.label)}`)}

        <div className="modal-body vx-prog">
          <div className={`vx-outcome ${OUTCOME.tone}`}>
            <span className="oi">{OUTCOME.icon}</span>
            <span>
              <span className="hl">{OUTCOME.hl}</span>
              <span className="sl">{OUTCOME.sl}</span>
            </span>
            {counts.total > 0 && (
              <span className="fails">
                {counts.timePosted} {tx('time', 'time')}<br />{counts.piecesPosted} {tx('pieces', 'pieces')}
              </span>
            )}
          </div>

          {startError && <div className="vx-note bad"><AlertTriangle /><span>{startError}</span></div>}

          {outcome === 'timeout' && (
            <div className="vx-note bad">
              <AlertTriangle />
              <span>
                <b>{tx('VistaNothingAfterTitle', 'Nothing after this point was attempted.')}</b>{' '}
                {tx('VistaNothingAfterBody',
                  'Vista did not answer for 15 minutes. Everything already posted is real and is in the batch — the rest of the crew never reached Vista.')}
              </span>
            </div>
          )}

          {(outcome === 'timeout' || outcome === 'partial' || outcome === 'failed') && counts.posted > 0 && (
            <div className="vx-note warn">
              <AlertTriangle />
              <span>
                <b>{tx('VistaBeforeRerunTitle', 'Before you re-run:')}</b>{' '}
                {batchId != null
                  ? tx('VistaBeforeRerunBody', `void or check batch ${batchId} in Vista. Re-running posts the entries that already made it a second time.`)
                    .replace('{batchId}', String(batchId))
                  : tx('VistaBeforeRerunNoId',
                    'a batch was created in Vista and this run did not report its ID. Check Vista for an incomplete batch for this crew — re-running posts the entries that already made it a second time.')}
              </span>
            </div>
          )}

          {/* Batch identifiers. Transcribed into Vista by hand, hence copy buttons. */}
          {(batchId != null || keyID != null) && (
            <div className="vx-batch">
              <span className="bl">Vista</span>
              {batchId != null && (
                <span className="vx-idbox">
                  <span>
                    <span className="k">{tx('BatchIDLabel', 'Batch ID')}</span>
                    <span className="v">{batchId}</span>
                  </span>
                  <button
                    className={`vx-copy${copied === 'Batch ID' ? ' done' : ''}`}
                    type="button"
                    aria-label={tx('CopyBatchId', 'Copy Batch ID')}
                    onClick={() => copy('Batch ID', String(batchId))}
                  >
                    {copied === 'Batch ID' ? <Check /> : <Copy />}
                  </button>
                </span>
              )}
              {keyID != null && (
                <span className="vx-idbox">
                  <span>
                    <span className="k">{tx('KeyIDLabel', 'Key ID')}</span>
                    <span className="v">{keyID}</span>
                  </span>
                  <button
                    className={`vx-copy${copied === 'Key ID' ? ' done' : ''}`}
                    type="button"
                    aria-label={tx('CopyKeyId', 'Copy Key ID')}
                    onClick={() => copy('Key ID', String(keyID))}
                  >
                    {copied === 'Key ID' ? <Check /> : <Copy />}
                  </button>
                </span>
              )}
              {outcome !== 'success' && (
                <span className="hint">{tx('VistaPartialBatchHint', 'A partial batch is still a real batch in Vista.')}</span>
              )}
            </div>
          )}

          {unknownBatch && batchId == null && (
            <div className="vx-note info">
              <Info />
              <span>
                {tx('VistaBatchIdUnavailable',
                  'A batch was created in Vista, but this run did not report its ID. Check Vista for an incomplete batch for this crew.')}
              </span>
            </div>
          )}

          {failureGroups.length > 0 && (
            <div className="vx-fgroup">
              {failureGroups.map((g) => {
                const open = openGroups.has(g.key);
                return (
                  <div key={g.key} className={`vx-fg${open ? ' open' : ''}`}>
                    <button
                      className="vx-fg-head"
                      type="button"
                      onClick={() => setOpenGroups((prev) => {
                        const next = new Set(prev);
                        if (next.has(g.key)) next.delete(g.key); else next.add(g.key);
                        return next;
                      })}
                    >
                      <span className="chev"><ChevronRight /></span>
                      <span>
                        <span className="t">{g.title}</span>
                        <span className="d">{g.plain}</span>
                      </span>
                      <span className="n">{g.total} {tx('failed', 'failed')}</span>
                    </button>
                    {open && (
                      <div className="vx-fg-body">
                        <div className="vx-subhead">{tx('WhoItHit', 'Who it hit')}</div>
                        <table className="vx-ptbl">
                          <tbody>
                            {g.hits.map((h, i) => (
                              <tr key={`${h.altEmployeeID}-${i}`}>
                                <td style={{ fontFamily: 'var(--f-sans)' }}>
                                  {h.employeeName}{h.altEmployeeID && ` #${h.altEmployeeID}`}
                                </td>
                                <td>{h.count} {h.source === 'time' ? tx('entries', 'entries') : tx('posts', 'posts')}</td>
                                <td style={{ fontFamily: 'var(--f-sans)' }}>{h.detail ?? tx('NoReasonGiven', 'no reason given')}</td>
                                <td>
                                  <button
                                    className="lnk"
                                    type="button"
                                    onClick={() => setErrorDetail({
                                      title: g.title,
                                      meta: [h.employeeName, h.altEmployeeID && `#${h.altEmployeeID}`,
                                        h.orderNo != null ? `order ${h.orderNo}` : ''].filter(Boolean).join(' · '),
                                      plain: g.plain,
                                      vistaSaid: h.detail,
                                      raw: h.errorDetails ?? h.errorMessage,
                                      onClose: () => setErrorDetail(null),
                                    })}
                                  >
                                    {tx('ViewError', 'View error')}
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {warnings.length > 0 && (
            <div className="vx-note warn">
              <AlertTriangle />
              <span>
                <b>{tx('VistaCheckThese', 'Posted, but check these.')}</b>
                <span className="vx-warnlist" style={{ marginTop: 7 }}>
                  {warnings.map((w, i) => (
                    <span className="vx-warnrow" key={i}><AlertTriangle /><span><b>{w.employeeName}</b> {w.text}</span></span>
                  ))}
                </span>
              </span>
            </div>
          )}

          {miscBanner(miscSkipped)}

          {rd?.error && outcome !== 'partial' && outcome !== 'success' && (
            <details className="vx-details">
              <summary><Info /> {tx('TechnicalDetails', 'Technical details')}</summary>
              <pre>{rd.error}</pre>
            </details>
          )}

          {!!job.progressData?.employees?.length && (
            <details className="vx-details">
              <summary>
                <ListChecks /> {tx('PerEmployeeDetail', 'Per-employee detail')} ({job.progressData.employees.length})
              </summary>
              <div style={{ padding: 12 }}>{renderEmployeeTable()}</div>
            </details>
          )}
        </div>

        <div className="modal-foot tr-exp-foot">
          <button className="tr-btn" onClick={downloadStatusExcel} disabled={excelBusy} type="button">
            <FileSpreadsheet /> {excelBusy ? tx('Generating', 'Generating…') : tx('ExportStatusToExcel', 'Export Status to Excel')}
          </button>
          <button className="tr-btn primary" onClick={onClose} type="button">
            <Check /> {tx('Done', 'Done')}
          </button>
        </div>
      </>
    );
  }
};

export default VistaExportModal;
